(() => {
  if (window.logoShirtsArticleInitialized) return;
  window.logoShirtsArticleInitialized = true;
  const observers = new WeakMap();
  const faqControls = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const normalize = (text) => (text || '').trim().toLocaleLowerCase().replace(/^\d+[.)]\s*/, '').replace(/[^\p{L}\p{N}]+/gu, '');
  const fragmentId = (href) => {
    if (!href) return '';
    const fragment = href.replace(/^#+/, '');
    try { return decodeURIComponent(fragment); } catch { return fragment; }
  };

  function getCandidateHeadings(content) {
    if (!content) return [];
    const standard = Array.from(content.querySelectorAll('h2, h3, h4')).filter((h) => h.textContent.trim() && !h.closest('.blog-featured-product'));
    if (standard.length >= 2) return standard;

    const strongs = [];
    const elements = content.querySelectorAll('strong, b');
    elements.forEach((el) => {
      if (el.closest('.blog-featured-product') || el.closest('a')) return;
      const text = el.textContent.trim();
      if (text.length >= 5 && text.length <= 110 && !/^tl;?dr$/i.test(text)) {
        strongs.push(el);
      }
    });

    return strongs.length > 0 ? strongs : standard;
  }

  function initialize(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const content = root.querySelector('.article-page__content');
    const toc = root.querySelector('.article-toc');
    const sidebar = root.querySelector('.article-page__sidebar');
    const container = root.querySelector('.article-page__container');
    const hasSidebarNote = !!root.querySelector('.article-sidebar-note');

    if (toc && content) {
      const headings = getCandidateHeadings(content);
      headings.forEach((heading, index) => {
        if (!heading.id) {
          const base = `${root.id}-heading-${index + 1}`;
          let id = base;
          let suffix = 1;
          while (document.getElementById(id)) id = `${base}-${suffix++}`;
          heading.id = id;
        }
      });

      const list = toc.querySelector('.article-toc__list');
      let links = list ? Array.from(list.querySelectorAll('a')) : [];

      if (links.length) {
        links.forEach((link, index) => {
          const id = fragmentId(link.getAttribute('href'));
          const linkText = (link.querySelector('.article-toc__text') || link).textContent.trim();
          const normText = normalize(linkText);

          let target = id ? document.getElementById(id) : null;
          if (target && !content.contains(target)) target = null;

          if (!target && id) {
            target = content.querySelector(`[name="${CSS.escape(id)}"]`);
          }

          if (!target && normText) {
            target = headings.find((h) => normalize(h.textContent) === normText);
          }

          if (!target && normText && normText.length > 4) {
            target = headings.find((h) => {
              const hNorm = normalize(h.textContent);
              return hNorm.includes(normText) || normText.includes(hNorm);
            });
          }

          if (!target && headings[index]) {
            target = headings[index];
          }

          if (target) {
            if (!target.id) {
              target.id = id || `${root.id}-section-${index + 1}`;
            }
            link.setAttribute('href', `#${encodeURIComponent(target.id)}`);
          }
        });
      } else if (headings.length > 0) {
        headings.forEach((heading, index) => {
          const item = document.createElement('li');
          item.className = 'article-toc__item';
          const link = document.createElement('a');
          link.className = 'article-toc__link';
          link.setAttribute('href', `#${encodeURIComponent(heading.id)}`);
          const number = document.createElement('span');
          number.className = 'article-toc__number';
          number.textContent = String(index + 1).padStart(2, '0');
          const text = document.createElement('span');
          text.className = 'article-toc__text';
          text.textContent = heading.textContent.trim();
          link.append(number, text);
          item.append(link);
          list.append(item);
        });
      }

      links = list ? Array.from(list.querySelectorAll('a')) : [];
      const hasLinks = links.length > 0;
      toc.hidden = !hasLinks;

      if (!hasLinks && !hasSidebarNote) {
        if (sidebar) sidebar.hidden = true;
        if (container) container.classList.add('article-page__container--no-sidebar');
      } else {
        if (sidebar) sidebar.hidden = false;
        if (container) container.classList.remove('article-page__container--no-sidebar');
      }

      const targets = links
        .map((link) => {
          const id = fragmentId(link.getAttribute('href'));
          return (
            (id ? document.getElementById(id) : null) ||
            (id ? content.querySelector(`[name="${CSS.escape(id)}"]`) : null) ||
            headings.find((h) => normalize(h.textContent) === normalize((link.querySelector('.article-toc__text') || link).textContent))
          );
        })
        .filter(Boolean);

      targets.forEach((target) => target.classList.add('article-page__anchor'));

      toc.addEventListener('click', (event) => {
        const link = event.target.closest('.article-toc__link');
        if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const id = fragmentId(link.getAttribute('href'));
        const linkText = (link.querySelector('.article-toc__text') || link).textContent.trim();
        const normText = normalize(linkText);

        let target = id ? document.getElementById(id) : null;
        if (!target && content && id) {
          target = content.querySelector(`[name="${CSS.escape(id)}"]`);
        }
        if (!target && content && normText) {
          target = headings.find((h) => normalize(h.textContent) === normText);
        }
        if (!target || !content.contains(target)) return;

        event.preventDefault();
        event.stopPropagation();
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({preventScroll: true});

        const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--theme-header-height')) || 120;
        const targetPos = target.getBoundingClientRect().top + window.scrollY - headerHeight - 24;

        window.scrollTo({
          top: Math.max(0, targetPos),
          behavior: reducedMotion.matches ? 'instant' : 'smooth'
        });

        links.forEach((item) => {
          item.classList.toggle('active', item === link);
          if (item === link) item.setAttribute('aria-current', 'location');
          else item.removeAttribute('aria-current');
        });
        try { window.history.replaceState(window.history.state, '', link.getAttribute('href')); } catch {}
      });

      if ('IntersectionObserver' in window && targets.length > 0) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            links.forEach((link) => {
              const active = fragmentId(link.getAttribute('href')) === entry.target.id;
              link.classList.toggle('active', active);
              if (active) link.setAttribute('aria-current', 'location');
              else link.removeAttribute('aria-current');
            });
          });
        }, {rootMargin: '-120px 0px -55% 0px', threshold: 0});
        targets.forEach((target) => observer.observe(target));
        observers.set(root, observer);
      }
    }
    root.querySelectorAll('.article-faq__item').forEach((item) => {
      const summary = item.querySelector('summary');
      let animation;
      let expanded = item.open;
      const setExpanded = (next, animate = true) => {
        const startHeight = item.getBoundingClientRect().height;
        animation?.cancel();
        animation = null;
        expanded = next;
        summary.setAttribute('aria-expanded', String(next));
        item.dataset.expanded = String(next);
        if (!animate || reducedMotion.matches || !item.animate) {
          item.open = next;
          return;
        }
        item.open = true;
        const borderHeight = item.offsetHeight - item.clientHeight;
        const endHeight = next ? item.getBoundingClientRect().height : summary.getBoundingClientRect().height + borderHeight;
        animation = item.animate([{height: `${startHeight}px`}, {height: `${endHeight}px`}], {
          duration: 280,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
          fill: 'both'
        });
        animation.onfinish = () => {
          item.open = next;
          animation.cancel();
          animation = null;
        };
      };
      summary.addEventListener('click', (event) => {
        event.preventDefault();
        setExpanded(!expanded);
      });
      faqControls.set(item, {setExpanded, cancel: () => animation?.cancel()});
    });
    root.querySelectorAll('.article-share__button').forEach((button) => {
      button.addEventListener('click', async () => {
        const url = button.dataset.url || document.querySelector('link[rel="canonical"]')?.href || window.location.href;
        const status = button.parentElement.querySelector('[role="status"]');
        status.textContent = '';
        try {
          if (navigator.share) {
            await navigator.share({title: root.querySelector('h1')?.textContent || document.title, url});
          } else if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(url);
            status.textContent = button.dataset.copied;
          } else {
            status.textContent = button.dataset.failed;
          }
        } catch (error) {
          if (error.name !== 'AbortError') status.textContent = button.dataset.failed;
        }
      });
    });
  }
  function initializeAll(scope) {
    scope.querySelectorAll('.article-page').forEach(initialize);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeAll(document), {once: true});
  else initializeAll(document);
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('.article-page').forEach((root) => observers.get(root)?.disconnect());
    event.target.querySelectorAll('.article-faq__item').forEach((item) => faqControls.get(item)?.cancel());
  });
  document.addEventListener('shopify:block:select', (event) => {
    const item = event.target.closest('.article-faq__item');
    if (item) {
      const control = faqControls.get(item);
      if (control) control.setExpanded(true, false);
      else item.open = true;
    }
  });
})();
